import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  FileSpreadsheet, FileText, Download, ChevronDown, TrendingUp,
  Users, Stethoscope, Building, UserCheck, Loader2, BarChart3, Hospital, DollarSign, CalendarDays
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { token } = useAuth();
  const [selectedReport, setSelectedReport] = useState('income');
  const [period, setPeriod] = useState('month');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Custom date range state
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showCustomDateDialog, setShowCustomDateDialog] = useState(false);
  
  // IGSS specific state
  const [igssUnits, setIgssUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('all');

  // Accounts Receivable specific state - Filters
  const [arFilters, setArFilters] = useState({
    medicos: [],
    instituciones: []
  });
  const [selectedArMedico, setSelectedArMedico] = useState('all');
  const [selectedArInstitucion, setSelectedArInstitucion] = useState('all');
  const [selectedArMonth, setSelectedArMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Accounts Receivable - Payment Modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    monto: '',
    metodo_pago: 'Efectivo',
    notas: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const reports = [
    { id: 'income', name: 'Ingresos', icon: TrendingUp, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { id: 'patients', name: 'Pacientes Atendidos', icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { id: 'exams', name: 'Exámenes Realizados', icon: Stethoscope, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { id: 'institution', name: 'Por Institución', icon: Building, color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { id: 'doctor', name: 'Por Médico Referente', icon: UserCheck, color: 'text-rose-600', bgColor: 'bg-rose-50' },
    { id: 'igss', name: 'IGSS', icon: Hospital, color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { id: 'accounts_receivable', name: 'Cuentas por Cobrar', icon: FileText, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  ];

  // Generate periods including previous months, quarters, semesters
  const generatePeriods = () => {
    const periods = [
      { value: 'day', label: 'Hoy', group: 'actual' },
      { value: 'week', label: 'Esta Semana', group: 'actual' },
      { value: 'month', label: 'Este Mes', group: 'actual' },
    ];
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    
    // Add quarters (Q1, Q2, Q3, Q4) for current and previous year
    const quarterNames = ['Q1 (Ene-Mar)', 'Q2 (Abr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dic)'];
    
    // Current year quarters (only past quarters)
    for (let q = currentQuarter; q >= 1; q--) {
      const startMonth = (q - 1) * 3;
      const endMonth = q * 3 - 1;
      if (q < currentQuarter || (q === currentQuarter && currentMonth >= startMonth)) {
        periods.push({
          value: `quarter_${currentYear}_${q}`,
          label: `${quarterNames[q-1]} ${currentYear}`,
          group: 'trimestre'
        });
      }
    }
    
    // Previous year quarters
    for (let q = 4; q >= 1; q--) {
      periods.push({
        value: `quarter_${currentYear - 1}_${q}`,
        label: `${quarterNames[q-1]} ${currentYear - 1}`,
        group: 'trimestre'
      });
    }
    
    // Add semesters
    const semesterNames = ['1er Semestre (Ene-Jun)', '2do Semestre (Jul-Dic)'];
    
    // Current year semesters
    const currentSemester = currentMonth < 6 ? 1 : 2;
    for (let s = currentSemester; s >= 1; s--) {
      if (s < currentSemester || (s === 1 && currentMonth >= 0) || (s === 2 && currentMonth >= 6)) {
        periods.push({
          value: `semester_${currentYear}_${s}`,
          label: `${semesterNames[s-1]} ${currentYear}`,
          group: 'semestre'
        });
      }
    }
    
    // Previous year semesters
    periods.push({
      value: `semester_${currentYear - 1}_2`,
      label: `${semesterNames[1]} ${currentYear - 1}`,
      group: 'semestre'
    });
    periods.push({
      value: `semester_${currentYear - 1}_1`,
      label: `${semesterNames[0]} ${currentYear - 1}`,
      group: 'semestre'
    });
    
    // Add previous months (last 12 months)
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    for (let i = 1; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const value = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      periods.push({ value, label: `${monthName} ${year}`, group: 'mes' });
    }
    
    // Add custom range option
    periods.push({ value: 'custom', label: '📅 Rango Personalizado...', group: 'custom' });
    
    return periods;
  };

  const periods = generatePeriods();
  
  // Get period label for display
  const getPeriodLabel = () => {
    if (period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      return `${customDateRange.startDate} al ${customDateRange.endDate}`;
    }
    const found = periods.find(p => p.value === period);
    return found?.label || period;
  };

  useEffect(() => {
    // Don't fetch if custom period selected but no dates set
    if (period === 'custom' && (!customDateRange.startDate || !customDateRange.endDate)) {
      return;
    }
    fetchReport();
  }, [selectedReport, period, selectedUnit, selectedArMedico, selectedArInstitucion, selectedArMonth, customDateRange]);

  useEffect(() => {
    if (selectedReport === 'igss') {
      fetchIgssUnits();
    }
    if (selectedReport === 'accounts_receivable') {
      fetchArFilters();
    }
  }, [selectedReport]);

  const fetchArFilters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/accounts-receivable/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArFilters({
        medicos: response.data.medicos || [],
        instituciones: response.data.instituciones || []
      });
    } catch (error) {
      console.error('Error fetching AR filters:', error);
    }
  };

  const fetchIgssUnits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/igss-units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIgssUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching IGSS units:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = '';
      let periodParam = period;
      
      // Handle custom date range
      if (period === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        periodParam = `custom_${customDateRange.startDate}_${customDateRange.endDate}`;
      }
      
      if (selectedReport === 'igss') {
        url = `${API_URL}/api/reports/igss?period=${periodParam}`;
        if (selectedUnit && selectedUnit !== 'all') {
          url += `&unidad_referente=${encodeURIComponent(selectedUnit)}`;
        }
      } else if (selectedReport === 'accounts_receivable') {
        // For accounts receivable, use selected month and filters
        const [year, month] = selectedArMonth.split('-').map(Number);
        url = `${API_URL}/api/accounts-receivable/monthly-report?year=${year}&month=${month}`;
        
        // Add optional filters
        if (selectedArMedico && selectedArMedico !== 'all') {
          url += `&medico_referente=${encodeURIComponent(selectedArMedico)}`;
        }
        if (selectedArInstitucion && selectedArInstitucion !== 'all') {
          url += `&institucion=${encodeURIComponent(selectedArInstitucion)}`;
        }
      } else {
        const endpoints = {
          income: '/api/reports/income',
          patients: '/api/reports/patients-attended',
          exams: '/api/reports/exams',
          institution: '/api/reports/by-institution',
          doctor: '/api/reports/by-doctor',
        };
        url = `${API_URL}${endpoints[selectedReport]}?period=${periodParam}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error al cargar el informe');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
    }).format(amount || 0);
  };

  // Format date for display (dd/MM/yy)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  // Payment modal functions
  const openPaymentModal = (account) => {
    setSelectedAccount(account);
    setPaymentForm({
      monto: account.monto_pendiente?.toString() || '',
      metodo_pago: 'Efectivo',
      notas: ''
    });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedAccount(null);
    setPaymentForm({ monto: '', metodo_pago: 'Efectivo', notas: '' });
  };

  const handleRegisterPayment = async () => {
    if (!selectedAccount || !paymentForm.monto) {
      toast.error('Ingrese el monto del pago');
      return;
    }

    const monto = parseFloat(paymentForm.monto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }

    if (monto > selectedAccount.monto_pendiente) {
      toast.error(`El monto no puede exceder el pendiente (${formatCurrency(selectedAccount.monto_pendiente)})`);
      return;
    }

    setSavingPayment(true);
    try {
      await axios.post(
        `${API_URL}/api/accounts-receivable/${selectedAccount.id}/abono`,
        null,
        {
          params: {
            monto: monto,
            metodo_pago: paymentForm.metodo_pago,
            notas: paymentForm.notas
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Pago registrado exitosamente');
      closePaymentModal();
      fetchReport(); // Refresh the report
    } catch (error) {
      console.error('Error registering payment:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setSavingPayment(false);
    }
  };

  const getReportTitle = () => {
    const report = reports.find(r => r.id === selectedReport);
    return `${report?.name || ''} - ${getPeriodLabel()}`;
  };

  const exportToExcel = () => {
    if (!reportData) return;
    
    let data = [];
    let filename = '';
    
    switch (selectedReport) {
      case 'income':
        filename = `Ingresos_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME DE INGRESOS - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Total Ingresos:', formatCurrency(reportData.total_income)],
          ['Pacientes Atendidos:', reportData.patient_count],
          [],
          ['DESGLOSE POR FORMA DE PAGO'],
          ['Método', 'Total'],
          ...(reportData.by_payment_method || []).map(m => [m.method, formatCurrency(m.total)]),
        ];
        break;
        
      case 'patients':
        filename = `Pacientes_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME DE PACIENTES ATENDIDOS - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Total Pacientes:', reportData.total_patients],
          [],
          ['LISTADO DE PACIENTES'],
          ['Fecha', 'Nombre', 'Edad', 'Examen', 'Institución'],
          ...(reportData.patients || []).map(p => [
            p.fecha, 
            p.nombre, 
            p.edad, 
            p.examenes?.length > 0 ? p.examenes.map(e => e.tipo_examen).join(', ') : p.tipo_examen,
            p.institucion || ''
          ]),
        ];
        break;
        
      case 'exams':
        filename = `Examenes_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME DE EXÁMENES REALIZADOS - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Total Exámenes:', reportData.total_exams],
          ['Ingresos por Exámenes:', formatCurrency(reportData.total_revenue)],
          [],
          ['DESGLOSE POR TIPO DE EXAMEN'],
          ['Tipo de Examen', 'Cantidad', 'Ingresos'],
          ...(reportData.by_exam_type || []).map(e => [e.exam_type, e.count, formatCurrency(e.revenue)]),
        ];
        break;
        
      case 'institution':
        filename = `PorInstitucion_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME POR INSTITUCIÓN - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Total Pacientes:', reportData.total_patients],
          [],
          ['DESGLOSE POR INSTITUCIÓN'],
          ['Institución', 'Pacientes', 'Ingresos'],
          ...(reportData.by_institution || []).map(i => [i.institution, i.count, formatCurrency(i.revenue)]),
        ];
        break;
        
      case 'doctor':
        filename = `PorMedico_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME POR MÉDICO REFERENTE - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Total Pacientes Referidos:', reportData.total_patients],
          [],
          ['DESGLOSE POR MÉDICO'],
          ['Médico Referente', 'Pacientes', 'Ingresos', 'Comisión (13%)'],
          ...(reportData.by_doctor || []).map(d => [d.doctor, d.count, formatCurrency(d.revenue), formatCurrency(d.commission)]),
        ];
        break;
        
      case 'igss':
        filename = `IGSS_${selectedUnit === 'all' ? 'Todas' : selectedUnit}_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
        data = [
          ['INFORME IGSS - ' + getReportTitle()],
          ['Período:', `${reportData.start_date} al ${reportData.end_date}`],
          ['Unidad Referente:', reportData.unidad_referente],
          ['Total Pacientes:', reportData.total_patients],
          [],
          ['Total con IVA:', formatCurrency(reportData.totals?.total_con_iva)],
          ['Total sin IVA:', formatCurrency(reportData.totals?.total_sin_iva)],
          ['Total IVA:', formatCurrency(reportData.totals?.total_iva)],
          [],
          ['DETALLE DE PACIENTES'],
          ['No.', 'SPS-465', 'Paciente', 'Afiliación', 'Código IGSS', 'Procedimiento', 'Fecha', 'Edad', 'NOG', 'Precio c/IVA', 'Precio s/IVA', 'IVA'],
          ...(reportData.patients || []).map(p => [
            p.numero,
            p.sps_465,
            p.paciente,
            p.afiliacion,
            p.codigo_igss || '',
            p.procedimiento,
            p.fecha,
            p.edad,
            p.nog || '',
            formatCurrency(p.precio_con_iva),
            formatCurrency(p.precio_sin_iva),
            formatCurrency(p.iva)
          ]),
          [],
          ['', '', '', '', '', '', '', '', 'TOTALES:', 
            formatCurrency(reportData.totals?.total_con_iva),
            formatCurrency(reportData.totals?.total_sin_iva),
            formatCurrency(reportData.totals?.total_iva)
          ],
        ];
        break;

      case 'accounts_receivable':
        filename = `CuentasPorCobrar_${format(new Date(), 'yyyy-MM')}`;
        const formatDateExcel = (d) => d ? d.split('-').reverse().join('/') : '-';
        data = [
          ['INFORME DE CUENTAS POR COBRAR - ' + (reportData.periodo || 'Este Mes')],
          [],
          ['Total Cuentas:', reportData.totals?.cantidad_cuentas || 0],
          ['Total Facturado:', formatCurrency(reportData.totals?.total_facturado || 0)],
          ['Total Cobrado:', formatCurrency(reportData.totals?.total_cobrado || 0)],
          ['Total Pendiente:', formatCurrency(reportData.totals?.total_pendiente || 0)],
          ['Cuentas Pagadas:', reportData.totals?.cuentas_pagadas || 0],
          ['Cuentas Vencidas:', reportData.totals?.cuentas_vencidas || 0],
          [],
          ['DETALLE DE CUENTAS'],
          ['Fecha', 'Paciente', 'Teléfono', 'Médico Referente', 'Institución', 'Examen', 'Total', 'Abonado', 'Pendiente', 'F. Límite', 'Estado'],
          ...(reportData.items || []).map(item => [
            formatDateExcel(item.fecha),
            item.nombre,
            item.telefono,
            item.medico_referente || '-',
            item.institucion || '-',
            item.tipo_examen || '-',
            formatCurrency(item.total),
            formatCurrency(item.total_abonos),
            formatCurrency(item.monto_pendiente),
            formatDateExcel(item.fecha_limite_pago),
            item.estado === 'pagado' ? 'Pagado' : item.vencido ? 'Vencido' : item.estado === 'parcial' ? 'Parcial' : 'Pendiente'
          ]),
        ];
        break;
    }
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Informe');
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const exportToPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF(selectedReport === 'igss' ? 'landscape' : 'portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(16);
    doc.setTextColor(26, 81, 146);
    doc.text('OFTALMODIAGNOSIS', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(getReportTitle().toUpperCase(), pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Período: ${reportData.start_date} al ${reportData.end_date}`, pageWidth / 2, 32, { align: 'center' });
    
    let yPos = 45;
    
    switch (selectedReport) {
      case 'income':
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`Total Ingresos: ${formatCurrency(reportData.total_income)}`, 14, yPos);
        doc.text(`Pacientes Atendidos: ${reportData.patient_count}`, 14, yPos + 7);
        yPos += 20;
        
        doc.autoTable({
          startY: yPos,
          head: [['Forma de Pago', 'Total']],
          body: (reportData.by_payment_method || []).map(m => [m.method, formatCurrency(m.total)]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146] },
        });
        break;
        
      case 'patients':
        doc.setFontSize(11);
        doc.text(`Total Pacientes: ${reportData.total_patients}`, 14, yPos);
        yPos += 15;
        
        doc.autoTable({
          startY: yPos,
          head: [['Fecha', 'Nombre', 'Edad', 'Examen']],
          body: (reportData.patients || []).slice(0, 50).map(p => [
            p.fecha, 
            p.nombre, 
            p.edad,
            p.examenes?.length > 0 ? p.examenes.map(e => e.tipo_examen).join(', ') : p.tipo_examen
          ]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146] },
        });
        break;
        
      case 'exams':
        doc.setFontSize(11);
        doc.text(`Total Exámenes: ${reportData.total_exams}`, 14, yPos);
        doc.text(`Ingresos: ${formatCurrency(reportData.total_revenue)}`, 14, yPos + 7);
        yPos += 20;
        
        doc.autoTable({
          startY: yPos,
          head: [['Tipo de Examen', 'Cantidad', 'Ingresos']],
          body: (reportData.by_exam_type || []).map(e => [e.exam_type, e.count, formatCurrency(e.revenue)]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146] },
        });
        break;
        
      case 'institution':
        doc.setFontSize(11);
        doc.text(`Total Pacientes: ${reportData.total_patients}`, 14, yPos);
        yPos += 15;
        
        doc.autoTable({
          startY: yPos,
          head: [['Institución', 'Pacientes', 'Ingresos']],
          body: (reportData.by_institution || []).map(i => [i.institution, i.count, formatCurrency(i.revenue)]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146] },
        });
        break;
        
      case 'doctor':
        doc.setFontSize(11);
        doc.text(`Total Pacientes Referidos: ${reportData.total_patients}`, 14, yPos);
        yPos += 15;
        
        doc.autoTable({
          startY: yPos,
          head: [['Médico Referente', 'Pacientes', 'Ingresos', 'Comisión (13%)']],
          body: (reportData.by_doctor || []).map(d => [d.doctor, d.count, formatCurrency(d.revenue), formatCurrency(d.commission)]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146] },
        });
        break;
        
      case 'igss':
        doc.setFontSize(11);
        doc.text(`Unidad Referente: ${reportData.unidad_referente}`, 14, yPos);
        doc.text(`Total Pacientes: ${reportData.total_patients}`, 14, yPos + 7);
        yPos += 20;
        
        doc.autoTable({
          startY: yPos,
          head: [['No.', 'SPS-465', 'Paciente', 'Afiliación', 'Cód. IGSS', 'Procedimiento', 'Fecha', 'Edad', 'NOG', 'c/IVA', 's/IVA', 'IVA']],
          body: (reportData.patients || []).map(p => [
            p.numero,
            p.sps_465,
            p.paciente,
            p.afiliacion,
            p.codigo_igss || '',
            p.procedimiento,
            p.fecha,
            p.edad,
            p.nog || '',
            formatCurrency(p.precio_con_iva),
            formatCurrency(p.precio_sin_iva),
            formatCurrency(p.iva)
          ]),
          theme: 'striped',
          headStyles: { fillColor: [26, 81, 146], fontSize: 6 },
          styles: { fontSize: 6 },
          foot: [['', '', '', '', '', '', '', '', 'TOTALES:', 
            formatCurrency(reportData.totals?.total_con_iva),
            formatCurrency(reportData.totals?.total_sin_iva),
            formatCurrency(reportData.totals?.total_iva)
          ]],
          footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        });
        break;
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generado el ${format(new Date(), "dd/MM/yyyy 'a las' HH:mm")} - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`Informe_${selectedReport}_${period}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exportado correctamente');
  };

  const renderReportContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#1a5192]" />
        </div>
      );
    }

    if (!reportData) return null;

    // IGSS Report specific rendering
    if (selectedReport === 'igss') {
      return (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-teal-50 border-teal-200">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-teal-700">{reportData.total_patients}</p>
                <p className="text-sm text-teal-600 mt-1">Pacientes IGSS</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-emerald-700">{formatCurrency(reportData.totals?.total_con_iva)}</p>
                <p className="text-sm text-emerald-600 mt-1">Total con IVA</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(reportData.totals?.total_sin_iva)}</p>
                <p className="text-sm text-blue-600 mt-1">Total sin IVA</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-amber-700">{formatCurrency(reportData.totals?.total_iva)}</p>
                <p className="text-sm text-amber-600 mt-1">IVA (12%)</p>
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card className="bg-white border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-teal-600 text-white">
                    <tr>
                      <th className="text-left p-3 font-semibold">No.</th>
                      <th className="text-left p-3 font-semibold">SPS-465</th>
                      <th className="text-left p-3 font-semibold">Paciente</th>
                      <th className="text-left p-3 font-semibold">Afiliación</th>
                      <th className="text-left p-3 font-semibold">Código IGSS</th>
                      <th className="text-left p-3 font-semibold">Procedimiento</th>
                      <th className="text-left p-3 font-semibold">Fecha</th>
                      <th className="text-center p-3 font-semibold">Edad</th>
                      <th className="text-left p-3 font-semibold">NOG</th>
                      <th className="text-right p-3 font-semibold">Precio c/IVA</th>
                      <th className="text-right p-3 font-semibold">Precio s/IVA</th>
                      <th className="text-right p-3 font-semibold">IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.patients?.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-medium">{item.numero}</td>
                        <td className="p-3">{item.sps_465}</td>
                        <td className="p-3 font-medium">{item.paciente}</td>
                        <td className="p-3">{item.afiliacion}</td>
                        <td className="p-3">{item.codigo_igss}</td>
                        <td className="p-3">{item.procedimiento}</td>
                        <td className="p-3">{formatDateDisplay(item.fecha)}</td>
                        <td className="p-3 text-center">{item.edad}</td>
                        <td className="p-3">{item.nog}</td>
                        <td className="p-3 text-right font-semibold text-emerald-600">{formatCurrency(item.precio_con_iva)}</td>
                        <td className="p-3 text-right">{formatCurrency(item.precio_sin_iva)}</td>
                        <td className="p-3 text-right text-amber-600">{formatCurrency(item.iva)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-semibold">
                    <tr>
                      <td colSpan="9" className="p-3 text-right">TOTALES:</td>
                      <td className="p-3 text-right text-emerald-700">{formatCurrency(reportData.totals?.total_con_iva)}</td>
                      <td className="p-3 text-right">{formatCurrency(reportData.totals?.total_sin_iva)}</td>
                      <td className="p-3 text-right text-amber-700">{formatCurrency(reportData.totals?.total_iva)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Accounts Receivable Report
    if (selectedReport === 'accounts_receivable') {
      const formatDateDisplay = (dateStr) => {
        if (!dateStr) return '-';
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      };

      return (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-700">{reportData.totals?.cantidad_cuentas || 0}</p>
                <p className="text-sm text-orange-600 mt-1">Total Cuentas</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(reportData.totals?.total_facturado || 0)}</p>
                <p className="text-sm text-blue-600 mt-1">Total Facturado</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(reportData.totals?.total_cobrado || 0)}</p>
                <p className="text-sm text-emerald-600 mt-1">Total Cobrado</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{formatCurrency(reportData.totals?.total_pendiente || 0)}</p>
                <p className="text-sm text-red-600 mt-1">Total Pendiente</p>
              </CardContent>
            </Card>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-amber-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Cuentas Pendientes</p>
                  <p className="text-2xl font-bold text-amber-600">{reportData.totals?.cuentas_pendientes || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Cuentas Pagadas</p>
                  <p className="text-2xl font-bold text-emerald-600">{reportData.totals?.cuentas_pagadas || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Cuentas Vencidas</p>
                  <p className="text-2xl font-bold text-red-600">{reportData.totals?.cuentas_vencidas || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Detalle de Cuentas por Cobrar - {reportData.periodo || 'Este Mes'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700">
                      <th className="p-3 text-left font-semibold">Fecha</th>
                      <th className="p-3 text-left font-semibold">Paciente</th>
                      <th className="p-3 text-left font-semibold">Teléfono</th>
                      <th className="p-3 text-left font-semibold">Médico Ref.</th>
                      <th className="p-3 text-left font-semibold">Institución</th>
                      <th className="p-3 text-left font-semibold">Examen</th>
                      <th className="p-3 text-right font-semibold">Total</th>
                      <th className="p-3 text-right font-semibold">Abonado</th>
                      <th className="p-3 text-right font-semibold">Pendiente</th>
                      <th className="p-3 text-center font-semibold">F. Límite</th>
                      <th className="p-3 text-center font-semibold">Estado</th>
                      <th className="p-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.items || []).length === 0 ? (
                      <tr>
                        <td colSpan="12" className="p-8 text-center text-slate-400">
                          No hay cuentas por cobrar en este período
                        </td>
                      </tr>
                    ) : (
                      (reportData.items || []).map((item, index) => (
                        <tr key={item.id || index} className={`border-b border-slate-100 hover:bg-slate-50 ${item.vencido ? 'bg-red-50' : ''}`}>
                          <td className="p-3 font-mono text-slate-600">{formatDateDisplay(item.fecha)}</td>
                          <td className="p-3 font-medium text-slate-900">{item.nombre}</td>
                          <td className="p-3 text-slate-600">{item.telefono}</td>
                          <td className="p-3 text-slate-600">{item.medico_referente || '-'}</td>
                          <td className="p-3 text-slate-600">{item.institucion || '-'}</td>
                          <td className="p-3 text-slate-600">{item.tipo_examen || '-'}</td>
                          <td className="p-3 text-right font-semibold">{formatCurrency(item.total)}</td>
                          <td className="p-3 text-right text-emerald-600">{formatCurrency(item.total_abonos)}</td>
                          <td className="p-3 text-right font-semibold text-red-600">{formatCurrency(item.monto_pendiente)}</td>
                          <td className="p-3 text-center font-mono text-sm">
                            {item.fecha_limite_pago ? formatDateDisplay(item.fecha_limite_pago) : '-'}
                          </td>
                          <td className="p-3 text-center">
                            {item.estado === 'pagado' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Pagado</Badge>
                            ) : item.vencido ? (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Vencido</Badge>
                            ) : item.estado === 'parcial' ? (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Parcial</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {item.estado !== 'pagado' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => openPaymentModal(item)}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Pagar
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Standard reports rendering
    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {selectedReport === 'income' && (
            <>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency(reportData.total_income)}</p>
                  <p className="text-sm text-emerald-600 mt-1">Total Ingresos</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-blue-700">{reportData.patient_count}</p>
                  <p className="text-sm text-blue-600 mt-1">Pacientes</p>
                </CardContent>
              </Card>
            </>
          )}
          {selectedReport === 'patients' && (
            <Card className="bg-blue-50 border-blue-200 col-span-2">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-blue-700">{reportData.total_patients}</p>
                <p className="text-sm text-blue-600 mt-1">Pacientes Atendidos</p>
              </CardContent>
            </Card>
          )}
          {selectedReport === 'exams' && (
            <>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-purple-700">{reportData.total_exams}</p>
                  <p className="text-sm text-purple-600 mt-1">Total Exámenes</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency(reportData.total_revenue)}</p>
                  <p className="text-sm text-emerald-600 mt-1">Ingresos</p>
                </CardContent>
              </Card>
            </>
          )}
          {selectedReport === 'institution' && (
            <Card className="bg-amber-50 border-amber-200 col-span-2">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-amber-700">{reportData.total_patients}</p>
                <p className="text-sm text-amber-600 mt-1">Total Pacientes</p>
              </CardContent>
            </Card>
          )}
          {selectedReport === 'doctor' && (
            <Card className="bg-rose-50 border-rose-200 col-span-2">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-bold text-rose-700">{reportData.total_patients}</p>
                <p className="text-sm text-rose-600 mt-1">Pacientes Referidos</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Data Table */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  {selectedReport === 'income' && (
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Forma de Pago</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Total</th>
                    </tr>
                  )}
                  {selectedReport === 'patients' && (
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Fecha</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Pacientes</th>
                    </tr>
                  )}
                  {selectedReport === 'exams' && (
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Tipo de Examen</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Cantidad</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Ingresos</th>
                    </tr>
                  )}
                  {selectedReport === 'institution' && (
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Institución</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Pacientes</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Ingresos</th>
                    </tr>
                  )}
                  {selectedReport === 'doctor' && (
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Médico Referente</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Pacientes</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Ingresos</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Comisión (13%)</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {selectedReport === 'income' && reportData.by_payment_method?.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">{item.method}</td>
                      <td className="p-4 text-right font-semibold text-emerald-600">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  {selectedReport === 'patients' && reportData.by_day?.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">{item.date}</td>
                      <td className="p-4 text-right font-semibold">{item.count}</td>
                    </tr>
                  ))}
                  {selectedReport === 'exams' && reportData.by_exam_type?.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">{item.exam_type}</td>
                      <td className="p-4 text-right">{item.count}</td>
                      <td className="p-4 text-right font-semibold text-emerald-600">{formatCurrency(item.revenue)}</td>
                    </tr>
                  ))}
                  {selectedReport === 'institution' && reportData.by_institution?.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">{item.institution}</td>
                      <td className="p-4 text-right">{item.count}</td>
                      <td className="p-4 text-right font-semibold text-emerald-600">{formatCurrency(item.revenue)}</td>
                    </tr>
                  ))}
                  {selectedReport === 'doctor' && reportData.by_doctor?.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">{item.doctor}</td>
                      <td className="p-4 text-right">{item.count}</td>
                      <td className="p-4 text-right font-semibold">{formatCurrency(item.revenue)}</td>
                      <td className="p-4 text-right font-semibold text-rose-600">{formatCurrency(item.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-[#1a5192]" />
            Informes
          </h2>
          <p className="text-sm text-slate-500 mt-1">Genera y exporta reportes de la clínica</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* IGSS Unit Filter - Only show for IGSS report */}
          {selectedReport === 'igss' && (
            <Select value={selectedUnit} onValueChange={setSelectedUnit}>
              <SelectTrigger className="w-[200px] bg-white" data-testid="igss-unit-select">
                <SelectValue placeholder="Unidad Referente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las unidades</SelectItem>
                {igssUnits.map((unit) => (
                  <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Accounts Receivable Filters */}
          {selectedReport === 'accounts_receivable' && (
            <>
              {/* Month Selector */}
              <input
                type="month"
                value={selectedArMonth}
                onChange={(e) => setSelectedArMonth(e.target.value)}
                className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1a5192]"
                data-testid="ar-month-select"
              />
              
              {/* Doctor Filter */}
              <Select value={selectedArMedico} onValueChange={setSelectedArMedico}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="ar-medico-select">
                  <SelectValue placeholder="Médico Referente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los médicos</SelectItem>
                  {arFilters.medicos.map((medico) => (
                    <SelectItem key={medico} value={medico}>{medico}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Institution Filter */}
              <Select value={selectedArInstitucion} onValueChange={setSelectedArInstitucion}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="ar-institucion-select">
                  <SelectValue placeholder="Institución" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las instituciones</SelectItem>
                  {arFilters.instituciones.map((inst) => (
                    <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          
          {/* Period Selector - Hide for accounts receivable */}
          {selectedReport !== 'accounts_receivable' && (
            <Select 
              value={period} 
              onValueChange={(val) => {
                if (val === 'custom') {
                  setShowCustomDateDialog(true);
                } else {
                  setPeriod(val);
                }
              }}
            >
              <SelectTrigger className="w-[220px] bg-white" data-testid="report-period-select">
                <SelectValue>
                  {getPeriodLabel()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {/* Group: Actual */}
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Período Actual</div>
                {periods.filter(p => p.group === 'actual').map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
                
                {/* Group: Trimestres */}
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Trimestres</div>
                {periods.filter(p => p.group === 'trimestre').map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
                
                {/* Group: Semestres */}
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Semestres</div>
                {periods.filter(p => p.group === 'semestre').map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
                
                {/* Group: Meses */}
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Meses Anteriores</div>
                {periods.filter(p => p.group === 'mes').map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
                
                {/* Custom Range */}
                <div className="border-t mt-1 pt-1">
                  {periods.filter(p => p.group === 'custom').map((p) => (
                    <SelectItem key={p.value} value={p.value} className="text-blue-600 font-medium">{p.label}</SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          )}
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white"
                disabled={!reportData}
                data-testid="export-report-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} data-testid="export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exportar a Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exportar a PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex flex-wrap gap-2">
        {reports.map((report) => {
          const Icon = report.icon;
          const isActive = selectedReport === report.id;
          return (
            <Button
              key={report.id}
              variant={isActive ? 'default' : 'outline'}
              onClick={() => {
                setSelectedReport(report.id);
                if (report.id !== 'igss') {
                  setSelectedUnit('all');
                }
              }}
              className={isActive 
                ? 'bg-[#1a5192] text-white' 
                : `border-slate-200 ${report.color} hover:${report.bgColor}`
              }
              data-testid={`report-tab-${report.id}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {report.name}
            </Button>
          );
        })}
      </div>

      {/* Report Content */}
      {renderReportContent()}

      {/* Payment Modal for Accounts Receivable */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="w-5 h-5" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          
          {selectedAccount && (
            <div className="space-y-4">
              {/* Account Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Paciente:</span>
                  <span className="font-medium">{selectedAccount.nombre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Total:</span>
                  <span className="font-semibold">{formatCurrency(selectedAccount.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Abonado:</span>
                  <span className="text-emerald-600">{formatCurrency(selectedAccount.total_abonos)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-sm font-medium text-slate-700">Pendiente:</span>
                  <span className="font-bold text-red-600">{formatCurrency(selectedAccount.monto_pendiente)}</span>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_amount">Monto a Pagar *</Label>
                  <Input
                    id="payment_amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedAccount.monto_pendiente}
                    value={paymentForm.monto}
                    onChange={(e) => setPaymentForm({ ...paymentForm, monto: e.target.value })}
                    placeholder="0.00"
                    className="bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <Select
                    value={paymentForm.metodo_pago}
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, metodo_pago: value })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Tarjeta Crédito/Débito">Tarjeta Crédito/Débito</SelectItem>
                      <SelectItem value="Transferencia">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment_notes">Notas</Label>
                  <Textarea
                    id="payment_notes"
                    value={paymentForm.notas}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notas: e.target.value })}
                    placeholder="Observaciones del pago..."
                    className="bg-white resize-none"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closePaymentModal}>
              Cancelar
            </Button>
            <Button 
              onClick={handleRegisterPayment} 
              disabled={savingPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Registrar Pago
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog open={showCustomDateDialog} onOpenChange={setShowCustomDateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <CalendarDays className="w-5 h-5" />
              Rango de Fechas Personalizado
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Fecha Inicio</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={customDateRange.startDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Fecha Fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={customDateRange.endDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Quick selectors */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-500">Selección rápida:</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), 0, 1);
                    const end = new Date();
                    setCustomDateRange({
                      startDate: start.toISOString().split('T')[0],
                      endDate: end.toISOString().split('T')[0]
                    });
                  }}
                >
                  Año Actual
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear() - 1, 0, 1);
                    const end = new Date(now.getFullYear() - 1, 11, 31);
                    setCustomDateRange({
                      startDate: start.toISOString().split('T')[0],
                      endDate: end.toISOString().split('T')[0]
                    });
                  }}
                >
                  Año Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
                    setCustomDateRange({
                      startDate: start.toISOString().split('T')[0],
                      endDate: now.toISOString().split('T')[0]
                    });
                  }}
                >
                  Últimos 3 Meses
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
                    setCustomDateRange({
                      startDate: start.toISOString().split('T')[0],
                      endDate: now.toISOString().split('T')[0]
                    });
                  }}
                >
                  Últimos 6 Meses
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomDateDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                if (customDateRange.startDate && customDateRange.endDate) {
                  setPeriod('custom');
                  setShowCustomDateDialog(false);
                } else {
                  toast.error('Seleccione ambas fechas');
                }
              }}
              disabled={!customDateRange.startDate || !customDateRange.endDate}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Aplicar Filtro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
